import {
  CloudUploadOutlined,
  DeleteOutline,
  ImageOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { GlobalDataContext } from "../contexts/GlobalData";
import { SnackbarContext } from "../contexts/Snackbar";
import { DELETE_IMG, DOWNLOAD_IMG } from "../enums/ApiPaths";
import {
  isHeicFile,
  supportedImageTypes,
  supportedImageTypesWithExtensions,
} from "../enums/Consts";
import {
  bannerPreviewImageStyles,
  bannerPreviewStyles,
} from "../features/aboutUs/enums/BannerStyles";
import { useFetch } from "../hooks/Fetching";
import useConfirm from "../hooks/useConfirm";
import { useDeleteNoId } from "../hooks/useFormDelete";
import { snackbarSeverity } from "../hooks/useSnackbar";
import api from "../utils/api/authApi";
import { trimText } from "../utils/Functions";

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const PREVIEW_HEIGHT = 200;

const SUPPORTED_TYPES_LABEL = [
  ...new Set(
    supportedImageTypes.map((type) => type.split("image/").pop().toUpperCase()),
  ),
].join(", ");

// Checkerboard behind the preview, so a logo without a background visibly
// stays transparent instead of looking like it sits on white
const checkerboard = (theme) => {
  const square =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.06)"
      : "rgba(0, 0, 0, 0.05)";
  return {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(45deg, ${square} 25%, transparent 25%),
      linear-gradient(-45deg, ${square} 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, ${square} 75%),
      linear-gradient(-45deg, transparent 75%, ${square} 75%)`,
    backgroundSize: "16px 16px",
    backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
  };
};

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImageUpload({
  path,
  fileType,
  queryKey,
  uploadPermission,
  title,
  description,
}) {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const downloadImg = `${DOWNLOAD_IMG}${id}/${fileType}`;
  const deleteImg = `${DELETE_IMG}${id}/${fileType}`;

  const { data: file, isLoading: loadingThumbnail } = useFetch(
    downloadImg,
    null,
    queryKey,
  );

  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isUploadAllowed = isPermissionAllowed(uploadPermission);

  const deleting = useDeleteNoId(deleteImg, "ImageDeleteConfirmation");
  const {
    ConfirmationDialog: BannerPreview,
    confirm,
    getMessage,
    handleSetConfirmationSize,
  } = useConfirm(t("BannerPreviewMessage"), t("Accept"), t("Cancel"));

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreview(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleDelete = async () => {
    setLoading(true);
    await deleting.submitForm();
    queryClient.invalidateQueries([queryKey]);
    setLoading(false);
  };

  function handleSelectedFile(newFile) {
    if (!newFile) {
      return;
    }
    if (isHeicFile(newFile)) {
      handleSnackbar("HeicNotSupported", snackbarSeverity.ERROR);
      return;
    }
    if (!supportedImageTypes.includes(newFile.type)) {
      handleSnackbar("UnsupportedFileType", snackbarSeverity.ERROR);
      return;
    }
    if (newFile.size > MAX_FILE_SIZE_BYTES) {
      handleSnackbar(
        t("ImageTooLarge", { size: MAX_FILE_SIZE_MB }),
        snackbarSeverity.ERROR,
        "filled",
        true,
        false,
      );
      return;
    }
    setSelectedFile(newFile);
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    if (fileType === "ABOUT_US") {
      handleSetConfirmationSize("lg");
      getMessage(
        <Stack spacing={2} alignItems="center">
          <Typography>{t("BannerImagePreviewMessage")}</Typography>
          <Container sx={bannerPreviewStyles}>
            <img alt="" style={bannerPreviewImageStyles(selectedPreview)} />
          </Container>
        </Stack>,
      );
      const ans = await confirm();

      if (!ans) {
        return;
      }
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      setLoading(true);

      const response = await api.post(path, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        queryClient.setQueryData([queryKey], (oldData) => {
          const objectUrl = URL.createObjectURL(selectedFile);
          if (Array.isArray(oldData)) {
            return [...oldData, objectUrl];
          }

          if (oldData?.results) {
            return {
              ...oldData,
              results: [...oldData.results, objectUrl],
            };
          }

          return objectUrl;
        });

        handleSnackbar("", snackbarSeverity.SUCCESS);
        setSelectedFile(null);
      }
    } catch (e) {
      handleSnackbar(e?.response?.data, snackbarSeverity.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, height: "100%" }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          <ImagePreview
            image={selectedPreview ?? file}
            isPending={Boolean(selectedPreview)}
            isLoading={loadingThumbnail}
            onDelete={!selectedPreview && file ? handleDelete : null}
            deleting={loading}
          />

          {selectedFile ? (
            <SelectedFileBar
              selectedFile={selectedFile}
              loading={loading}
              disabled={!isUploadAllowed}
              onCancel={() => setSelectedFile(null)}
              onUpload={handleUpload}
            />
          ) : (
            <DropZone
              disabled={!isUploadAllowed}
              onFileSelected={handleSelectedFile}
            />
          )}
        </Stack>
      </Paper>
      <BannerPreview />
      <deleting.ConfirmationDialog />
    </Fragment>
  );
}

function ImagePreview({ image, isPending, isLoading, onDelete, deleting }) {
  const { t } = useTranslation();

  let content;
  if (isLoading) {
    content = <CircularProgress />;
  } else if (image) {
    content = (
      <Box
        component="img"
        src={image}
        alt=""
        sx={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  } else {
    content = (
      <Stack alignItems="center" spacing={1} color="text.secondary">
        <ImageOutlined sx={{ fontSize: 48, opacity: 0.6 }} />
        <Typography variant="body2">{t("NoImageUploaded")}</Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        ...checkerboard(theme),
        position: "relative",
        height: PREVIEW_HEIGHT,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      })}
    >
      {content}
      {isPending && (
        <Chip
          label={t("NotUploadedYet")}
          size="small"
          color="primary"
          sx={{ position: "absolute", top: 8, left: 8 }}
        />
      )}
      {onDelete && (
        <Tooltip title={t("Delete")}>
          <span style={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton
              size="small"
              color="error"
              onClick={onDelete}
              disabled={deleting}
              sx={{
                bgcolor: "background.paper",
                boxShadow: 1,
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

function DropZone({ disabled, onFileSelected }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (!disabled) {
      onFileSelected(event.dataTransfer.files?.[0]);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={openFilePicker}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openFilePicker();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          setDragActive(true);
        }
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
      sx={(theme) => ({
        border: "2px dashed",
        borderColor: dragActive ? "primary.main" : "divider",
        borderRadius: 2,
        px: 2,
        py: 3,
        textAlign: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        bgcolor: dragActive
          ? alpha(theme.palette.primary.main, 0.06)
          : "transparent",
        transition: theme.transitions.create(["border-color", "background-color"]),
        "&:hover, &:focus-visible": disabled
          ? {}
          : {
              borderColor: "primary.main",
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              outline: "none",
            },
      })}
    >
      <CloudUploadOutlined color="primary" sx={{ fontSize: 36, mb: 0.5 }} />
      <Typography variant="body1" fontWeight={500}>
        {t("DragnDropHere")}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t("ImageUploadLimits", {
          types: SUPPORTED_TYPES_LABEL,
          size: MAX_FILE_SIZE_MB,
        })}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={supportedImageTypesWithExtensions.join(",")}
        onChange={(event) => {
          onFileSelected(event.target.files?.[0]);
          // lets the same file be picked again after cancelling
          event.target.value = "";
        }}
      />
    </Box>
  );
}

function SelectedFileBar({ selectedFile, loading, disabled, onCancel, onUpload }) {
  const { t } = useTranslation();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
        <ImageOutlined color="primary" />
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {trimText(selectedFile.name, 40)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(selectedFile.size)}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={loading}>
          {t("Cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={onUpload}
          disabled={disabled}
          disableElevation
        >
          {t("Upload")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
